import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING } from "const"
import { CcLibraryHeading, CcLibraryItem } from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    DeleteManyCcLibraryHeadings,
    DeleteManyCcLibraryItems,
    UpdateManyCcLibraryHeadings,
    UpdateManyCcLibraryItems,
    setCcLibraryHeadingConfirmDelete,
    setCcLibraryItemReloadList,
} from "client/store"
import {
    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
    CheckButton,
    Button,
    SpinnerIcon,
    TableNestedIds,
    TableGroupedIds,
} from "client/components"

const ConfirmDelete = () => {
    const headingToDelete = useSelector((state: StoreType) => state.ccLibraryHeading.confirmDelete)
    const headingList = useSelector((state: StoreType) => state.ccLibraryHeading.headingList)
    const itemList = useSelector((state: StoreType) => state.ccLibraryItem.itemList)
    const nested = useSelector((state: StoreType) => state.ccLibraryItem.itemGroupedIds)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [deleteChildren, setDeleteChildren] = useState(false)
    const [deleteState, setDeleteState] = useState<StateType>(IDLE)

    const onCancel = () => {
        dispatch(setCcLibraryHeadingConfirmDelete(null))
        setDeleteChildren(false)
    }

    const onConfirm = () => {
        setDeleteState(PENDING)

        const deleteHeadings: number[] = [headingToDelete.id]
        const deleteItems: number[] = []
        const updateHeadings: CcLibraryHeading[] = []
        const updateItems: CcLibraryItem[] = []

        // find nested heading
        let nestedHeading: TableGroupedIds = null
        let nestedHeadingParent: TableGroupedIds = null

        const searchHeading = (nestedHeadings: readonly TableGroupedIds[]) => {
            nestedHeadings.every((row) => {
                if (row.groupId === headingToDelete.id) {
                    nestedHeading = row
                    return false // break
                }
                if (row.groupId === headingToDelete.headingId) nestedHeadingParent = row
                if (!row.groupId && !headingToDelete.headingId) nestedHeadingParent = row // root
                searchHeading(row.nestedGroupedIds)
                return true // continue
            })
        }
        searchHeading([nested])


        // delete nested children
        if (deleteChildren) {
            const recursiveDeleteItem = (nestedItems: readonly TableNestedIds[]) => {
                nestedItems.forEach((row) => {
                    deleteItems.push(Number(row.id))
                    recursiveDeleteItem(row.nestedIds)
                })
            }

            const recursiveDeleteParagraph = (nestedHeadings: readonly TableGroupedIds[]) => {
                nestedHeadings.forEach((row) => {
                    deleteHeadings.push(row.groupId)
                    recursiveDeleteItem(row.nestedIds)
                    recursiveDeleteParagraph(row.nestedGroupedIds)
                })
            }

            recursiveDeleteItem(nestedHeading.nestedIds)
            recursiveDeleteParagraph(nestedHeading.nestedGroupedIds)

        // move nested children
        } else {
            // define new heading order
            const headingLength = nestedHeadingParent.nestedGroupedIds.length
            const lastNestedHeading = headingLength && nestedHeadingParent.nestedGroupedIds[headingLength - 1]
            const lastHeading = lastNestedHeading ? headingList[lastNestedHeading.groupId] : null
            const headingOrder = lastHeading ? lastHeading.order + 1 : 0

            // define new item order
            const itemLength = nestedHeadingParent.nestedIds.length
            const lastNestedItem = itemLength && nestedHeadingParent.nestedIds[itemLength - 1]
            const lastItem = lastNestedItem ? itemList[Number(lastNestedItem.id)] : null
            const itemOrder = lastItem ? lastItem.order + 1 : 0

            // move child paragraphs to upper level
            nestedHeading.nestedGroupedIds.forEach((row) => {
                const headingObject: CcLibraryHeading = {
                    ...headingList[row.groupId],
                    headingId: headingToDelete.headingId,
                    order: headingOrder + updateHeadings.length,
                }
                updateHeadings.push(headingObject)
            })

            // move child items to upper level
            nestedHeading.nestedIds.forEach((row) => {
                const itemObject: CcLibraryItem = {
                    ...itemList[Number(row.id)],
                    headingId: headingToDelete.headingId,
                    order: itemOrder + updateItems.length,
                }
                updateItems.push(itemObject)
            })
        }

        Promise.all([
            DeleteManyCcLibraryHeadings(deleteHeadings),
            DeleteManyCcLibraryItems(deleteItems),
            UpdateManyCcLibraryHeadings(updateHeadings),
            UpdateManyCcLibraryItems(updateItems),
        ]).then(() => {
            setDeleteState(IDLE)
            setDeleteChildren(false)
            dispatch(setCcLibraryHeadingConfirmDelete(null))
            dispatch(setCcLibraryItemReloadList(false))
            setTimeout(() => {
                dispatch(setCcLibraryItemReloadList(true))
            }, 0)
        })
    }

    return (
        <ConfirmWrapper
            show={headingToDelete !== null}
            onClose={onCancel}
        >
            <Confirm align="start">
                <ConfirmTitle>{headingToDelete && headingToDelete.title}</ConfirmTitle>
                <ConfirmDescription>{lang.deleteParagraphConfirm}</ConfirmDescription>

                <CheckButton
                    checked={deleteChildren}
                    onChange={() => setDeleteChildren(!deleteChildren)}
                    title={lang.deleteChildItems}
                />

                <ConfirmBottom>
                    <Button row onClick={onCancel}>{lang.cancel}</Button>
                    <Button variant="success" row onClick={onConfirm}>
                        {deleteState === PENDING ? <SpinnerIcon /> : lang.confirm}
                    </Button>
                </ConfirmBottom>
            </Confirm>
        </ConfirmWrapper>
    )
}
export default ConfirmDelete
